import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import { feedPosts } from "../../mocks/explore";

interface Post {
  id: number;
  user: { name: string; avatar: string };
  challenge: string;
  prompt: string;
  image?: string | null;
  text?: string;
  likes: number;
  comments: number;
  claps: number;
  height: string;
}

function FeedCard({ post, onCopyPrompt }: { post: Post; onCopyPrompt: (p: string) => void }) {
  const [claps, setClaps] = useState(post.claps);
  const [likes, setLikes] = useState(post.likes);
  const [liked, setLiked] = useState(false);
  const [clapped, setClapped] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopyPrompt(post.prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden break-inside-avoid mb-5 group hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
      style={{ boxShadow: "0 2px 12px rgba(25,31,40,0.06)" }}
    >
      {/* Image */}
      {post.image && (
        <div className="overflow-hidden">
          <img
            src={post.image}
            alt={post.challenge}
            className="w-full object-cover object-top group-hover:scale-102 transition-transform duration-500"
            style={{ maxHeight: post.height === "tall" ? "300px" : "200px" }}
          />
        </div>
      )}

      {/* Text post bg */}
      {!post.image && post.text && (
        <div className="bg-gradient-to-br from-[#F2F4F6] to-[#EAFFF5] px-5 pt-5 pb-3">
          <p className="text-[#191F28] text-[15px] font-medium leading-[1.7]">
            {post.text}
          </p>
        </div>
      )}

      {/* Card Body */}
      <div className="p-4">
        {/* User */}
        <div className="flex items-center gap-2.5 mb-3">
          <img
            src={post.user.avatar}
            alt={post.user.name}
            className="w-8 h-8 rounded-full object-cover object-top"
          />
          <div>
            <p className="text-sm font-bold text-[#191F28] leading-none">{post.user.name}</p>
            <p className="text-xs text-[#8B95A1] mt-0.5">{post.challenge}</p>
          </div>
        </div>

        {/* Prompt Preview */}
        <div className="bg-[#F2F4F6] rounded-xl p-3 mb-3">
          <p className="text-xs text-[#8B95A1] font-medium mb-1 flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-terminal-box-line text-[10px]" />
            </span>
            사용한 프롬프트
          </p>
          <p className="text-xs text-[#3D4756] font-mono leading-relaxed line-clamp-2">
            {post.prompt}
          </p>
        </div>

        {/* Copy Prompt Button — MAIN CTA */}
        <button
          onClick={handleCopy}
          className={`w-full py-2.5 rounded-full text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap mb-3 ${
            copied
              ? "bg-[#00D873] text-white"
              : "bg-[#E8F5E9] text-[#00D873] hover:bg-[#00D873] hover:text-white"
          }`}
        >
          {copied ? "✓ 프롬프트 복사됨!" : "✨ 프롬프트 가져오기"}
        </button>

        {/* Reactions */}
        <div className="flex items-center justify-between border-t border-[#F2F4F6] pt-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setLiked(!liked); setLikes(l => liked ? l - 1 : l + 1); }}
              className={`flex items-center gap-1.5 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${liked ? "text-[#E91E63]" : "text-[#8B95A1] hover:text-[#E91E63]"}`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={liked ? "ri-heart-fill" : "ri-heart-line"} />
              </span>
              {likes}
            </button>
            <button
              onClick={() => { setClapped(!clapped); setClaps(c => clapped ? c - 1 : c + 1); }}
              className={`flex items-center gap-1.5 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${clapped ? "text-[#FF8C00]" : "text-[#8B95A1] hover:text-[#FF8C00]"}`}
            >
              <span>👏</span>
              {claps}
            </button>
            <button className="flex items-center gap-1.5 text-xs font-semibold text-[#8B95A1] hover:text-[#191F28] transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-chat-3-line" />
              </span>
              {post.comments}
            </button>
          </div>
          <button className="w-7 h-7 flex items-center justify-center text-[#8B95A1] hover:text-[#00D873] transition-colors cursor-pointer rounded-full hover:bg-[#E8F5E9]">
            <i className="ri-share-forward-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}

const sortOptions = ["최신순", "인기순", "도움됨"];

export default function FeedPage() {
  const navigate = useNavigate();
  const [sort, setSort] = useState("최신순");
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);
  const [filterTag, setFilterTag] = useState("전체");

  const tags = ["전체", "이미지 생성", "업무 자동화", "영상 편집", "글쓰기"];

  const handleCopyPrompt = (prompt: string) => {
    setCopiedPrompt(prompt);
    setTimeout(() => setCopiedPrompt(null), 3000);
  };

  // Split into two columns for masonry
  const col1 = feedPosts.filter((_, i) => i % 2 === 0);
  const col2 = feedPosts.filter((_, i) => i % 2 === 1);

  return (
    <div className="min-h-screen bg-[#F2F4F6]">
      <Navbar />

      {/* Copied Toast */}
      {copiedPrompt && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#191F28] text-white px-6 py-3.5 rounded-2xl text-sm font-bold flex items-center gap-2.5 animate-fade-in-up shadow-xl">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-checkbox-circle-fill text-[#00D873] text-lg" />
          </div>
          프롬프트가 클립보드에 복사됐어요!
        </div>
      )}

      <div className="pt-20 pb-16 px-6">
        <div className="max-w-5xl mx-auto">

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mt-6 mb-8">
            <div>
              <p className="text-[#00D873] text-xs font-bold tracking-widest uppercase mb-2">Social Feed</p>
              <h1 className="text-4xl md:text-5xl font-extrabold text-[#191F28] leading-tight">
                오늘 그로세오에서<br />만들어진 것들
              </h1>
              <p className="text-[#8B95A1] text-sm mt-2">
                실제 챌린저들의 결과물과 프롬프트를 구경하고 따라해보세요 ✨
              </p>
            </div>
            <button
              onClick={() => navigate("/explore")}
              className="flex items-center gap-2 bg-[#00D873] text-white text-sm font-bold px-6 py-3 rounded-full cursor-pointer hover:opacity-90 transition-all whitespace-nowrap self-start md:self-auto flex-shrink-0"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line" />
              </span>
              나도 도전하기
            </button>
          </div>

          {/* Filter & Sort Bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex gap-2 flex-wrap">
              {tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setFilterTag(tag)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    filterTag === tag
                      ? "bg-[#191F28] text-white"
                      : "bg-white text-[#8B95A1] hover:text-[#191F28]"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-white rounded-full p-1 flex-shrink-0">
              {sortOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setSort(opt)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    sort === opt
                      ? "bg-[#191F28] text-white"
                      : "text-[#8B95A1] hover:text-[#191F28]"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Featured Post */}
          <div className="bg-white rounded-3xl overflow-hidden mb-8 flex flex-col md:flex-row">
            <div className="md:w-[45%] overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/44df4028b85597588fdfe4c83345999d.jpg"
                alt="featured"
                className="w-full h-64 md:h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 p-8 flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold text-[#00D873] bg-[#E8F5E9] px-3 py-1.5 rounded-full">
                  🏆 이번 주 베스트
                </span>
                <h2 className="text-xl font-extrabold text-[#191F28] mt-4 mb-2">
                  이 프롬프트 하나로 SNS 팔로워 1,000명 늘었어요
                </h2>
                <p className="text-[#8B95A1] text-sm leading-relaxed mb-5">
                  스테이블 디퓨전 챌린지에 참여한 이소연님의 결과물입니다.
                  단 하나의 프롬프트로 이런 퀄리티를 만들 수 있다는 게 놀랍죠?
                </p>
                <div className="bg-[#F2F4F6] rounded-xl p-3.5 mb-6">
                  <p className="text-xs text-[#8B95A1] mb-1.5">사용한 프롬프트</p>
                  <p className="text-xs text-[#3D4756] font-mono leading-relaxed">
                    ethereal forest with glowing mushrooms and fireflies, fantasy illustration, watercolor style, pastel dream --v 6
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button className="flex-1 bg-[#00D873] text-white font-bold py-3 rounded-full text-sm cursor-pointer hover:opacity-90 transition-all whitespace-nowrap">
                  ✨ 프롬프트 가져오기
                </button>
                <button className="flex items-center gap-1.5 text-sm text-[#8B95A1] font-semibold hover:text-[#E91E63] transition-colors cursor-pointer whitespace-nowrap">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-heart-line" />
                  </span>
                  215
                </button>
              </div>
            </div>
          </div>

          {/* Masonry Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              {col1.map((post) => (
                <FeedCard key={post.id} post={post} onCopyPrompt={handleCopyPrompt} />
              ))}
            </div>
            <div>
              {col2.map((post) => (
                <FeedCard key={post.id} post={post} onCopyPrompt={handleCopyPrompt} />
              ))}
            </div>
          </div>

          {/* Load More */}
          <div className="text-center mt-10">
            <button className="bg-white text-[#191F28] font-bold px-10 py-4 rounded-full text-sm border border-[#E0E2E6] hover:border-[#00D873] hover:text-[#00D873] transition-all cursor-pointer whitespace-nowrap">
              더 많은 결과물 보기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
